import { useCallback, useEffect, useRef, useState } from "react";
import { useLeague } from "./useLeague";
import { apiGet } from "../utils/apiClient";

interface PolledRequestState<T> {
  data?: T;
  error?: Error;
  /** When the last successful response came back, for an "updated" indicator. */
  updatedAt?: number;
  /** True while a refresh is in flight, after the first response has landed. */
  isRefreshing: boolean;
  /** Fetch again now, without waiting for the next interval. */
  refresh: () => void;
}

/**
 * Re-reads a URL on an interval, for data that changes while someone watches
 * it — the score and clock of a game in progress.
 *
 * Unlike `useRequest` this does not cache, since the point is to see the
 * current value. Polling stops while the tab is hidden and fetches once
 * immediately when it comes back, so a page left open in a background tab is
 * not asking the API for a score nobody is looking at, and is never stale on
 * return.
 *
 * Two separate controls, because whether to read at all and how often to read
 * again are different questions:
 *
 * - `enabled: false` holds off entirely — there is nothing here to watch.
 * - `intervalMs: null` reads once and does not repeat. This is for a page that
 *   only learns whether it needs to keep watching from the first response: it
 *   reads the game, and starts polling only if that game turns out to be live.
 *   Turning the interval on afterwards schedules the repeat without re-reading.
 */
export function usePolledRequest<T = unknown>(
  url: string | undefined,
  token: string | undefined,
  intervalMs: number | null,
  enabled = true,
): PolledRequestState<T> {
  const league = useLeague();
  const leagueAbbr = league?.currentLeague?.abbr;

  const [state, setState] = useState<{
    data?: T;
    error?: Error;
    updatedAt?: number;
  }>({});
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [manualKey, setManualKey] = useState(0);

  // Lets a response that arrives after unmount, or after a newer read was
  // issued, be dropped instead of overwriting newer data.
  const latestRequest = useRef(0);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const active = Boolean(url) && enabled;

  const load = useCallback(() => {
    if (!url || !enabled) return;

    const id = ++latestRequest.current;
    const isCurrent = () => isMounted.current && latestRequest.current === id;

    setIsRefreshing(true);
    apiGet<T>(url, {
      token,
      ...(leagueAbbr ? { leagueAbbr } : {}),
    })
      .then((data) => {
        if (!isCurrent()) return;
        setState({ data, updatedAt: Date.now() });
      })
      .catch((error: unknown) => {
        if (!isCurrent()) return;
        // Keep whatever was last shown: a single failed poll should not blank
        // out a live score that was on screen a moment ago.
        setState((previous) => ({
          ...previous,
          error: error instanceof Error ? error : new Error(String(error)),
        }));
      })
      .finally(() => {
        if (!isCurrent()) return;
        setIsRefreshing(false);
      });
  }, [url, token, leagueAbbr, enabled]);

  // The read itself, on mount and whenever what is being read changes.
  useEffect(() => {
    load();
  }, [load, manualKey]);

  // The repeat, scheduled separately so switching the interval on does not
  // trigger another read of data that has just arrived.
  useEffect(() => {
    if (!active || intervalMs === null) return;

    let timer = window.setInterval(load, intervalMs);

    // A hidden tab stops asking, and catches up the moment it is looked at
    // again, so what comes back into view is never a stale score.
    const handleVisibility = () => {
      window.clearInterval(timer);
      if (document.hidden) return;
      load();
      timer = window.setInterval(load, intervalMs);
    };

    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [load, intervalMs, active]);

  return {
    ...state,
    isRefreshing: active && isRefreshing,
    refresh: useCallback(() => setManualKey((key) => key + 1), []),
  };
}
